import { IlamyCalendar, type Resource } from '@ilamy/calendar';
import dayjs from 'dayjs';
import { type ReactNode, useMemo, useRef } from 'react';

import { useElementSize } from '../lib/use-element-size.js';
import { useFillViewportHeight } from '../lib/use-fill-viewport-height.js';
import { cn } from '../lib/utils.js';
import { fromCalendarEvent } from './from-calendar-event.js';
import { schedulerDayCapacity } from './scheduler-day-capacity.js';
import { SCHEDULER_GRID_METRICS } from './scheduler-grid-metrics.js';
import { SCHEDULER_HATCHED_CLASS } from './scheduler-hatched-class.js';
import { schedulerMonthLabelClass } from './scheduler-month-label-class.js';
import type {
  DimeSchedulerProps,
  SchedulerItem,
  SchedulerResource,
  SchedulerView,
} from './scheduler.types.js';
import { SchedulerDateSync } from './SchedulerDateSync.js';
import { SchedulerHeader } from './SchedulerHeader.js';
import { toCalendarEvent } from './to-calendar-event.js';
import { useInertDraggableWrappers } from './use-inert-draggable-wrappers.js';

export function DimeScheduler<TPayload>({
  items,
  resources,
  date,
  view,
  orientation,
  weekStart = 0,
  loading = false,
  hideHeader = false,
  fillViewport = false,
  emptyMessage = 'Nothing scheduled in this range',
  isDayHatched,
  renderItem,
  renderResource,
  onRangeChange,
  onViewChange,
  onItemClick,
  onItemMove,
  onMoreClick,
}: DimeSchedulerProps<TPayload>): ReactNode {
  const rootRef = useRef<HTMLDivElement>(null);
  useInertDraggableWrappers(rootRef, onItemMove === undefined);
  const fills = fillViewport && view === 'month' && resources === undefined;
  const fillStyle = useFillViewportHeight(
    rootRef,
    fills,
    SCHEDULER_GRID_METRICS.weekRowCount * SCHEDULER_GRID_METRICS.rowMinHeight +
      SCHEDULER_GRID_METRICS.weekdayHeaderHeight,
  );
  const { ref: gridRef, height: gridHeight } = useElementSize<HTMLDivElement>();
  const hasResources = resources !== undefined && resources.length > 0;

  const visibleItems = useMemo(() => {
    if (!hasResources) {
      return items;
    }
    const resourceIds = new Set(resources.map((resource) => resource.id));
    return items.filter(
      (item) => item.resourceId !== undefined && resourceIds.has(item.resourceId),
    );
  }, [items, resources, hasResources]);

  const itemsById = useMemo(
    () => new Map<string, SchedulerItem<TPayload>>(visibleItems.map((item) => [item.id, item])),
    [visibleItems],
  );
  const events = useMemo(() => visibleItems.map((item) => toCalendarEvent(item)), [visibleItems]);

  const resourcesById = useMemo(
    () =>
      new Map<string, SchedulerResource>(
        (resources ?? []).map((resource) => [resource.id, resource]),
      ),
    [resources],
  );
  const calendarResources = useMemo<Resource[] | undefined>(
    () =>
      hasResources
        ? resources.map((resource) => ({ id: resource.id, title: resource.title }))
        : undefined,
    [resources, hasResources],
  );

  return (
    <div
      className="flex h-full min-h-0 flex-col gap-2 outline-none"
      tabIndex={-1}
      ref={rootRef}
      style={fillStyle}
      role="region"
      aria-label={view === 'month' ? dayjs(date).format('MMMM YYYY') : 'Schedule'}
      data-testid="dime-scheduler"
      aria-busy={loading}
    >
      {items.length === 0 && !loading && (
        <p role="status" className="text-sm text-muted-foreground">
          {emptyMessage}
        </p>
      )}
      <div
        ref={gridRef}
        className={cn('min-h-0 flex-1 transition-opacity', loading && 'opacity-60')}
      >
        <IlamyCalendar
          events={events}
          resources={calendarResources}
          orientation={orientation}
          initialDate={dayjs(date)}
          initialView={view}
          firstDayOfWeek={weekStart === 1 ? 'monday' : 'sunday'}
          headerComponent={
            <>
              <SchedulerDateSync date={date} view={view} />
              {!hideHeader && <SchedulerHeader />}
            </>
          }
          hideExportButton
          renderEventForm={() => null}
          disableCellClick
          disableEventClick={onItemClick === undefined}
          disableDragAndDrop={onItemMove === undefined}
          dayMaxEvents={fills ? schedulerDayCapacity(gridHeight) : undefined}
          getCellClassName={(info) => {
            const day = info.start.toDate();
            return cn(
              isDayHatched?.(day) && SCHEDULER_HATCHED_CLASS,
              view === 'month' &&
                !hasResources &&
                day.getDate() === 1 &&
                schedulerMonthLabelClass(day),
            );
          }}
          renderEvent={(event, segment) => {
            const item = itemsById.get(String(event.id));
            if (!item) {
              return null;
            }
            return renderItem(item, { segmentStart: segment.isStart, segmentEnd: segment.isEnd });
          }}
          renderResource={
            renderResource
              ? (resource) => {
                  const match = resourcesById.get(String(resource.id));
                  return match ? renderResource(match) : null;
                }
              : undefined
          }
          onDateChange={(_, range) =>
            onRangeChange?.({ start: range.start.toDate(), end: range.end.toDate() }, view)
          }
          onViewChange={(next) => onViewChange?.(next as SchedulerView)}
          onEventClick={(event) => {
            const item = itemsById.get(String(event.id));
            if (item) {
              onItemClick?.(item);
            }
          }}
          onEventUpdate={(event) => {
            const move = fromCalendarEvent(event, itemsById);
            if (move) {
              onItemMove?.(move);
            }
          }}
          onMoreEventsClick={(day, moreEvents) => {
            const active = document.activeElement;
            onMoreClick?.(
              day.toDate(),
              moreEvents.flatMap((event) => itemsById.get(String(event.id)) ?? []),
              // Safari and Firefox on macOS leave the clicked button unfocused, so `body` means no trigger.
              active instanceof HTMLElement && active !== document.body ? active : null,
            );
          }}
        />
      </div>
    </div>
  );
}
